import {
  ASSISTANT_SHARES_QUERY_KEY,
  CONNECTOR_APPROVAL_QUERY_KEY,
  EFFECTIVE_ORG_CONNECTOR_APPROVAL_QUERY_KEY,
  MEETING_BY_UUID_QUERY_KEY,
  MEETINGS_QUERY_KEY,
  NOTIFICATIONS_QUERY_KEY,
  NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY,
} from "@frontend-shared/constants";
import { ENotificationType } from "@frontend-shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  connectNotificationsSocket,
  disconnectNotificationsSocket,
  onMeetingStatusChanged,
  onNotificationCreated,
  onNotificationsSocketConnect,
  onOrganizationAccessChanged,
} from "@/lib/notifications-websocket";

import { invalidateOrganizationAccessQueries } from "../utils/invalidate-organization-access-queries";
import {
  playNotificationSound,
  primeAudioContextOnUserGesture,
} from "../utils/play-notification-sound";

interface IUseNotificationSocketProps {
  isAuthenticated: boolean;
}

// NOTE: Subscribes to the notifications WS namespace while the user is
// authenticated. On `notification:created`, invalidates the list + unread
// count queries so React Query refetches with the new row included. Keeps
// the wire-shape concern (Socket.io) out of the drawer components.
export const useNotificationSocket = ({
  isAuthenticated,
}: IUseNotificationSocketProps): void => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("notifications");

  // NOTE: The notifications socket "connect" event fires on the very first
  // connect too, not just reconnects. The meetings query already fetches on
  // mount, so skip that first connect and only invalidate on genuine
  // reconnects — that's where we might have missed a status push while down.
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) {
      hasConnectedRef.current = false;
      return undefined;
    }

    // NOTE: Browser autoplay policy keeps the AudioContext suspended until the
    // tab gets a real user gesture. Wire a one-shot unlocker now so the very
    // first WS notification chime can play even if it arrives before the user
    // explicitly clicks the notifications bell.
    primeAudioContextOnUserGesture();

    connectNotificationsSocket();

    const unsubscribe = onNotificationCreated((payload) => {
      queryClient.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
      queryClient.invalidateQueries({
        queryKey: [NOTIFICATIONS_UNREAD_COUNT_QUERY_KEY],
      });
      // NOTE: Only fires on real WS events — not on initial cache hydration or
      // React Query refetches — so the user doesn't get a chime on page load.
      playNotificationSound();

      // NOTE: A collaborator leaving an assistant changes its share list, but
      // the owner only learns via this notification — refetch the shares so an
      // already-open share dialog drops the departed user without a reload.
      const { notification } = payload;
      if (
        notification.type === ENotificationType.ASSISTANT_LEFT &&
        "assistantUuid" in notification.data
      ) {
        queryClient.invalidateQueries({
          queryKey: [
            ASSISTANT_SHARES_QUERY_KEY,
            notification.data.assistantUuid,
          ],
        });
      }

      // NOTE: NSAI-4090 — an org admin changing a connector's minimum approval
      // level rewrites the effective permission the settings page shows, but the
      // page had no way to learn about it and kept displaying the old value
      // until a manual reload. This push is that signal: invalidate the
      // effective-floor query AND the per-user settings query (the level the UI
      // renders is clamped against the floor) so an open Approval settings card
      // repaints on its own.
      if (
        notification.type === ENotificationType.CONNECTOR_PERMISSION_CHANGED
      ) {
        queryClient.invalidateQueries({
          queryKey: [EFFECTIVE_ORG_CONNECTOR_APPROVAL_QUERY_KEY],
        });
        queryClient.invalidateQueries({
          queryKey: [CONNECTOR_APPROVAL_QUERY_KEY],
        });
      }

      // NOTE: NSAI-3490 — a webhook trigger previously returned a bare 202
      // with no further signal. These toasts are the only real-time feedback
      // a webhook owner gets that their run actually finished (and how).
      if (
        notification.type === ENotificationType.WEBHOOK_RUN_SUCCEEDED &&
        "webhookName" in notification.data
      ) {
        toast.success(
          t("type.webhook_run_succeeded.toast", {
            webhookName: notification.data.webhookName,
          }),
        );
      } else if (
        notification.type === ENotificationType.WEBHOOK_RUN_FAILED &&
        "webhookName" in notification.data
      ) {
        // NOTE: A failed run isn't guaranteed to carry an error (e.g. a
        // timeout with no captured message) — data.error can be null, and
        // i18next would otherwise interpolate the literal string "null".
        toast.error(
          notification.data.error
            ? t("type.webhook_run_failed.toast", {
                webhookName: notification.data.webhookName,
                error: notification.data.error,
              })
            : t("type.webhook_run_failed.toast_no_error", {
                webhookName: notification.data.webhookName,
              }),
        );
      }
    });

    // NOTE: Ownership is granted/revoked by someone else, so the affected
    // user's cached `isOwner` goes stale with no local mutation to invalidate
    // it — every owner-gated surface (General's Edit/Delete, Members, Projects,
    // Roles, the invite dialog, the org tab list) keeps rendering the old
    // permission until a manual reload. This listens for the transient
    // ORGANIZATION_ACCESS_CHANGED push rather than the paired bell
    // notification, because the bell one is preference-gated: a user who muted
    // the ORGANIZATIONS category would never get it and would be stuck showing
    // permissions the server no longer grants. This hook is mounted app-wide in
    // AppLayout, so the refresh lands whichever page they happen to be on.
    // Ownership also re-grants project-admin across every project in the org,
    // hence the project/member keys alongside the org one (NSAI-3616).
    const unsubscribeOrganizationAccess = onOrganizationAccessChanged(() => {
      invalidateOrganizationAccessQueries(queryClient);
    });

    // NOTE: Replaces the old 10s poll on the call-assistant list. The server
    // pushes MEETING_STATUS_CHANGED on every status transition for this user's
    // meetings — invalidate the list (and any open meeting detail) so React
    // Query refetches the authoritative row immediately instead of on a timer.
    const unsubscribeMeetingStatus = onMeetingStatusChanged(() => {
      queryClient.invalidateQueries({ queryKey: [MEETINGS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MEETING_BY_UUID_QUERY_KEY] });
    });

    // NOTE: A push delivered while the socket was down is lost — there is no
    // replay. On a reconnect (not the initial connect), refetch to catch up on
    // anything missed during the disconnect window. Ownership is included
    // because a permission change missed here is worse than a stale meeting
    // row: the user would keep acting as an owner they no longer are
    // (NSAI-3616).
    const unsubscribeConnect = onNotificationsSocketConnect(() => {
      if (!hasConnectedRef.current) {
        hasConnectedRef.current = true;
        return;
      }
      queryClient.invalidateQueries({ queryKey: [MEETINGS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MEETING_BY_UUID_QUERY_KEY] });
      invalidateOrganizationAccessQueries(queryClient);
    });

    return () => {
      unsubscribe();
      unsubscribeOrganizationAccess();
      unsubscribeMeetingStatus();
      unsubscribeConnect();
      disconnectNotificationsSocket();
    };
  }, [isAuthenticated, queryClient, t]);
};
